// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle,
  HighlightOff,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { NetSuiteLogo } from '@/icons/netsuite-logo';
import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

interface NetSuiteCRMRowProps {
  isEnabledForOrg: boolean;
  isQuickBooksConnected: boolean;
}

/**
 * Table row component displaying NetSuite ERP integration status.
 * NetSuite integration is managed externally, so this is a read-only display.
 */
export function NetSuiteCRMRow(props: NetSuiteCRMRowProps) {
  const isUnavailable = !props.isEnabledForOrg;
  const isDisabledByOtherERP = props.isQuickBooksConnected;

  const DisabledCell = (cellProps: { text: string }) => (
    <TableCell>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}
      >
        <HighlightOff color={'disabled'} fontSize={'small'} sx={{ mr: 1 }} />
        {cellProps.text}
      </Typography>
    </TableCell>
  );

  if (isUnavailable) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Contact support to enable this integration" arrow>
              <NetSuiteLogo sx={{ width: 100, height: 24, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="NetSuite integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="NetSuite integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="NetSuite integration is not enabled for your organization, contact Hardfin support to activate" />
        <TableCell align={'right'}>{/* Unavailable - no settings */}</TableCell>
      </TableRow>
    );
  }

  if (isDisabledByOtherERP) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title="Only one accounting system can be connected at a time"
              arrow
            >
              <NetSuiteLogo sx={{ width: 100, height: 24, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="Not available while QuickBooks is already connected" />
        <DisabledCell text="Not available while QuickBooks is already connected" />
        <DisabledCell text="Not available while QuickBooks is already connected" />
        <TableCell align={'right'}>{/* Disabled - no settings */}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NetSuiteLogo sx={{ width: 100, height: 24 }} />
        </Box>
      </TableCell>
      <TableCell>
        <Typography sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircle color={'success'} fontSize={'small'} sx={{ mr: 1 }} />
          Connected
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ display: 'flex', alignItems: 'center' }}>
          Managed by Hardfin
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ display: 'flex', alignItems: 'center' }}>
          <SyncOutlined color={'success'} fontSize={'small'} sx={{ mr: 1 }} />
          Enabled
        </Typography>
      </TableCell>
      <TableCell align={'right'}>
        {/* Configuration managed by Hardfin */}
      </TableCell>
    </TableRow>
  );
}
